import React, { useEffect } from 'react'
import {
  Button,
  IconDotsThreeVertical,
  Text,
  csx,
  useBreakpoint,
} from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'

import { useOrderForm } from '../../../../core/requests/checkout'
import { EmptyCart, OptionsDrawer } from './components'
import {
  CartSummary,
  CheckoutButton,
  CheckoutButtonMobile,
  CheckoutProductList,
} from '../../components'
import { CartAlerts } from './components/CartAlerts'
import { useCart } from './hooks'
import { groupItemsWithAssemblyItems } from './utils/groupItemsWithAssemblyItems'
import { CartCard } from './components/CartCard'
import { useLinkStoreToSalesAppOrderForm } from '../../hooks/useLinkStoreToSalesAppOrderForm'
import { useVendor } from '../../../../core/globalState/vendor'
import { NodeId } from '../../../../core/utils/relay/NodeId'
import { removeGiftItemsFromCartItemsList } from './utils/removeGiftItemsFromCartItemsList'
import { GiftList } from './components/GiftList'
import { DefaultTopbar } from '../../../../core/components/TopbarVariations'
import { ExtensionPoint } from '../../../../core/components/ExtensionPoint'

export const CartScreen = () => {
  const { orderForm } = useOrderForm()
  const [linkStoreToSalesAppOrderForm] = useLinkStoreToSalesAppOrderForm()
  const { vendor } = useVendor()
  const { breakpoint } = useBreakpoint()
  const isMobileOrTablet = ['mobile', 'tablet'].includes(breakpoint)

  const {
    updateCartItem,
    removeCartItem,
    cartItems,
    handleContinueClick,
    handleOptionsClick,
    handleOptionsDrawerClose,
    currencyCode,
    cartHasItems,
    messages,
    isContinueButtonDisabled,
    isDrawerOptionsOpen,
  } = useCart()

  const cartItemsWithoutGifts = removeGiftItemsFromCartItemsList(cartItems)

  const items = groupItemsWithAssemblyItems(cartItemsWithoutGifts)

  const continueButtonProps = {
    onClick: handleContinueClick,
    disabled: isContinueButtonDisabled,
  }

  useEffect(() => {
    if (!orderForm?.orderFormId || !vendor?.storeId) return
    linkStoreToSalesAppOrderForm({
      variables: {
        input: {
          orderFormId: NodeId.encode('OrderForm', orderForm.orderFormId),
          storeMasterdataId: vendor.storeId,
        },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderForm?.orderFormId, vendor?.storeId])

  return (
    <>
      <DefaultTopbar />
      <div
        className={csx({
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        })}
      >
        <div
          className={csx({
            flex: 1,
            padding: '$space-6',
          })}
        >
          <div
            className={csx({
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            })}
          >
            <Text variant="display">
              <FormattedMessage id="cart" />
            </Text>
            <Button
              variant="tertiary"
              className={csx({ color: '$primary' })}
              icon={
                <IconDotsThreeVertical
                  color="black"
                  className={csx({
                    ':hover': { color: '$action.main.secondaryHover' },
                  })}
                />
              }
              onClick={handleOptionsClick}
              data-testid="cart-actions-options-button"
            />
          </div>
          {cartHasItems && (
            <Text variant="title2" tone="secondary">
              <FormattedMessage
                id="cart-content"
                values={{ quantity: cartItems.length }}
              />
            </Text>
          )}
          <CartAlerts className={csx({ paddingTop: '$space-4' })} />
          {cartHasItems ? (
            <div>
              <CheckoutProductList className={csx({ marginTop: '$space-8' })}>
                {items.map((item, index) => (
                  <CartCard
                    key={index}
                    item={item}
                    messages={messages}
                    currencyCode={currencyCode}
                    removeCartItem={removeCartItem}
                    updateCartItem={updateCartItem}
                  />
                ))}
              </CheckoutProductList>
              <GiftList className={csx({ marginTop: '$space-6' })} />
            </div>
          ) : (
            <EmptyCart />
          )}
        </div>
        {isMobileOrTablet && (
          <div
            className={csx({
              paddingTop: '$space-3',
              background: '$gray05',
            })}
          />
        )}
        <CartSummary>
          {isMobileOrTablet ? (
            <CheckoutButtonMobile {...continueButtonProps} />
          ) : (
            <CheckoutButton
              size="large"
              onClick={handleContinueClick}
              disabled={isContinueButtonDisabled}
              className={csx({
                width: '100%',
              })}
            >
              <FormattedMessage id="continue" />
            </CheckoutButton>
          )}
        </CartSummary>
        <OptionsDrawer
          isOpen={isDrawerOptionsOpen}
          onClose={handleOptionsDrawerClose}
        />
        <ExtensionPoint name="cart.product-list.after" />
      </div>
    </>
  )
}
